/*
 * From https://www.redblobgames.com/plotter/1723-procedural-river-growing/
 * Copyright 2022 Red Blob Games <redblobgames@gmail.com>
 * @license Apache-2.0 <https://www.apache.org/licenses/LICENSE-2.0.html>
 */

console.info("I'm happy to answer questions about the code; email me at redblobgames@gmail.com");

import TriangleMesh from '@redblobgames/dual-mesh';
import {createNoise2D} from 'simplex-noise';
import MeshBuilder from '@redblobgames/dual-mesh/create';
import Poisson from 'fast-2d-poisson-disk-sampling';

const MM_PER_INCH = 25.4;
const AXIDRAW_PER_MM = 80;
const PAPER_IN = {w: 11, h: 8.5};
const MARGIN_IN = {w: 0.5, h: 0.5};
const OUTER = {
    w: PAPER_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    h: PAPER_IN.h * MM_PER_INCH * AXIDRAW_PER_MM
};
const INNER = {
    left: MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    top: MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM,
    w: OUTER.w - 2*MARGIN_IN.w * MM_PER_INCH * AXIDRAW_PER_MM,
    h: OUTER.h - 2*MARGIN_IN.h * MM_PER_INCH * AXIDRAW_PER_MM
};

const PLOT_SCALE = 20.32;

const parameters = {
    width: Math.round(INNER.w / PLOT_SCALE),
    height: Math.round(INNER.h / PLOT_SCALE),
    spacing: 6,
};

function assignElevation(mesh: TriangleMesh) {
    let noise = createNoise2D();
    let t_elevation = new Float32Array(mesh.numTriangles);
    for (let t = 0; t < mesh.numTriangles; t++) {
        let [x, y] = mesh.t_pos([], t);

        let nx = x / parameters.width,
            ny = y / parameters.height;
        let n = (
                        noise(      2.5 * nx,       3.5 * ny)
                + 0.5 * noise(100 + 6.5 * nx, 100 + 4.5 * ny)
                + 0.5 * noise(200 + 3.5 * nx, 200 + 7.5 * ny)
        ) / 2;
        t_elevation[t] = (n + 1) / 2; // from -1:+1 to 0:1
    }
    return t_elevation;
}


/* Prefer picking elements of the queue with a lower priority */
function biased_search(mesh: TriangleMesh, seeds: number[], t_priority) {
    let t_outflow = new Int32Array(mesh.numTriangles);
    let t_watershed = new Int32Array(mesh.numTriangles);
    let t_watershed_flux: number[] = [];
    t_outflow.fill(-1);
    t_watershed.fill(-1);
    seeds.forEach((t) => { t_watershed[t] = t; t_outflow[t] = t; t_watershed_flux[t] = 0; });
    let t_queue = seeds.concat([]);
    for (let i = 0; i < t_queue.length; i++) {
        // No longer just a queue, let's pick in a biased way
        let pivot_step = Math.ceil((t_queue.length-i) / 4);
        for (let pivot = t_queue.length-1; pivot > i; pivot -= pivot_step) {
            if (t_priority[t_queue[pivot]] < t_priority[t_queue[i]]) {
                let swap = t_queue[pivot];
                t_queue[pivot] = t_queue[i];
                t_queue[i] = swap;
            }
        }
        
        let t_current = t_queue[i];
        for (let t_neighbor of mesh.t_circulate_t([], t_current)) {
            if (t_outflow[t_neighbor] === -1) {
                t_outflow[t_neighbor] = t_current;
                t_watershed[t_neighbor] = t_watershed[t_current];
                t_watershed_flux[t_watershed[t_neighbor]]++;
                t_queue.push(t_neighbor);
            }
        }
    }
    return {t_outflow, t_watershed, t_watershed_flux, t_order: t_queue};
}


// My Dual-Mesh create module has a way to add boundary points, but
// (1) it only works for 1000x1000 square regions, and (2) I think it
// may be wrong! So I'm using my own function here, and I will file a
// bug for fixing dual mesh.
function addBoundaryPoints(spacing, width, height) {
    const size = Math.max(width, height);
    let N = Math.ceil(size/spacing);
    let points: number[][] = [];
    for (let i = 0; i <= N; i++) {
        let t = i / N;
        let offset = 15 * (1 - Math.pow(t - 0.5, 2));
        let x = width * t;
        let y = height * t;
        points.push([-offset, y], [width+offset, y]);
        points.push([x, -offset], [x, height+offset]);
    }
    return points;
}


//////////////////////////////////////////////////////////////////////


let points = new Poisson({
    shape: [parameters.width, parameters.height],
    radius: parameters.spacing,
    tries: 6,
}).fill();

let builder = new MeshBuilder();
let boundaryPoints = addBoundaryPoints(parameters.spacing * 1.5, parameters.width, parameters.height);
builder.addPoints(boundaryPoints);
builder.numBoundaryRegions = boundaryPoints.length;
builder.addPoints(points);

let mesh: TriangleMesh = builder.create();

let svg = `<svg viewBox="0 0 ${OUTER.w} ${OUTER.h}" width="11in" height="8.5in">
   <g transform="translate(${INNER.left},${INNER.top}) scale(${PLOT_SCALE})">\n`

let t_elevation = assignElevation(mesh);
let {t_outflow} = biased_search(mesh, /* random */ [5, 8, 13], t_elevation);
for (let t1 = 0; t1 < mesh.numTriangles; t1++) {
    let p1 = mesh.t_pos([], t1);
    let t2 = t_outflow[t1];
    if (t2 >= 0) {
        let p2 = mesh.t_pos([], t2);
        let color = "black";
        if (mesh.t_circulate_r([], t1).some((r) => r <= mesh.numBoundaryRegions)) color = "none";
        if (mesh.t_circulate_r([], t2).some((r) => r <= mesh.numBoundaryRegions)) color = "none";
        if (color !== "none") {
            svg += `\n  <line x1="${p1[0]}" y1="${p1[1]}" x2="${p2[0]}" y2="${p2[1]}" stroke="${color}" />`;
        }
    }
}
// draw the boundary points:
/*
for (let r = 0; r < mesh.numSolidRegions; r++) {
    let p = mesh.r_pos([], r);
    if (r < mesh.numBoundaryRegions) svg += `<circle cx="${p[0]}" cy="${p[1]}" r="2" fill="blue" />`;
}
*/

svg += `\n\n</g></svg>`;

(document.querySelector("#plot") as HTMLElement).innerHTML = svg;
(document.querySelector("main textarea") as HTMLTextAreaElement).value = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>${svg}`;
